import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  ClientDTO,
  CreateParkingSubscriptionDTO,
  Currency,
  IParkingSubscriptionDTO,
  MultipleCarsPerUserTreatAs,
  ParkingSubscriptionType,
  UpdateParkingSubscriptionDTO,
  ParkingSubscriptionUsage,
  ParkingSubscriptionPaidBy,
  ParkingQuotaDuration,
} from 'src/app/base/models/models.swagger.generated';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin, merge, Observable, of, Subscription } from 'rxjs';
import { ToastService } from 'src/app/base/services/toast.service';
import { ClientService } from 'src/app/base/services/client/client.service';
import { map, startWith } from 'rxjs/operators';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { FormHelper } from 'src/app/base/shared/form-helper';
import { MultiFieldDurationPickerHelper } from 'src/app/base/shared/multi-field-duration-picker/multi-field-duration-picker-helper';
import { SubscriptionDetailsService } from './subscription-details.service';
import { AdministrationItemType, DetailsInitInfo } from '../../data-structures';
import { MaxParkingDurationInMinFormType, SubscriptionDetailsGeneralComponent } from './subscription-details-general/subscription-details-general.component';
import { TranslateModule } from '@ngx-translate/core';
import { SubscriptionDetailsTariffsComponent } from './subscription-details-tariffs/subscription-details-tariffs.component';
import { TabModule } from '@syncfusion/ej2-angular-navigations';
import { SubscriptionEditFormType } from './subscription-details-general/subscription-details-general-form.type';
import { MessageDirective, MessageSeverity, MessageVariant } from 'src/app/directives/message.directive';

@Component({
    selector: 'app-subscription-details',
    templateUrl: './subscription-details.component.html',
    styleUrls: ['./subscription-details.component.scss'],
    imports: [
        TabModule,
        SubscriptionDetailsGeneralComponent,
        SubscriptionDetailsTariffsComponent,
        TranslateModule,
        MessageDirective
    ]
})
export class SubscriptionDetailsComponent implements OnInit, OnDestroy {
  @Input() public set detailsInitInfo(value: DetailsInitInfo) {
    const valueStr = value ? JSON.stringify(value) : null;
    const detailsInitInfoStr = this._detailInitInfo ? JSON.stringify(this._detailInitInfo) : null;
    if (valueStr != detailsInitInfoStr) {
      this.subscriptionService.reset();
      this._detailInitInfo = value;
      if (value?.id) {
        forkJoin({subscriptionDTO: this.subscriptionService.getSubscription(value.id), isUsedInSessions: this.subscriptionService.isSubscriptionUsedInSessions(value.id)}).subscribe((data) => {
          this.selectedSubscription = data.subscriptionDTO;
          this.isUsedInSessions = data.isUsedInSessions;
          this.updateForm();
        });
        this.isAddSubscription = false;
      } else {
        this.isUsedInSessions = false;
        this.selectedSubscription = null;
        this.isAddSubscription = true;
        this.resetForm();
        this.subscriptionForm.controls.clientId.setValue(this._detailInitInfo.clientId);
      }
    }
  }

  public get isBusy(): boolean {
    return this.subscriptionService.isBusy;
  }

  public get canDelete(): boolean {
    return !this.isBusy && !this.selectedSubscription?.active;
  }

  public selectedSubscription: IParkingSubscriptionDTO;
  public isAddSubscription: boolean;

  @Output() public cancel = new EventEmitter<void>();
  @Output() public subscriptionCreated = new EventEmitter<IParkingSubscriptionDTO>();
  @Output() public subscriptionUpdated = new EventEmitter<IParkingSubscriptionDTO>();
  @Output() public subscriptionDeleted = new EventEmitter<number>();
  @Output() public hasPendingDetailChangesChanged = new EventEmitter<boolean>();

  public subscriptionForm: FormGroup<SubscriptionEditFormType> = this.fb.group<SubscriptionEditFormType>({
    clientId: this.fb.control<number>(null),
    active: this.fb.control<boolean>(true, Validators.required),
    assignable: this.fb.control<boolean>(true, Validators.required),
    name: this.fb.control<string>(null, Validators.required),
    description: this.fb.control<string>(null),
    multipleCarsPerUserTreatAs: this.fb.control<MultipleCarsPerUserTreatAs>(MultipleCarsPerUserTreatAs.Forbidden, Validators.required),
    currency: this.fb.control<Currency>(Currency.CHF, Validators.required),
    type: this.fb.control<ParkingSubscriptionType>(ParkingSubscriptionType.StandardSubscription, Validators.required),
    companyId: this.fb.control<number>(null),
    denyForeignParkingSubscriptionFallback: this.fb.control<boolean>(false, Validators.required),
    chargeCapPerMonth: this.fb.control<boolean>(false, Validators.required),
    chargeCap: this.fb.control<number>(null, [Validators.required, Validators.min(0)]),
    chargeInRelationToEmploymentPercentage: this.fb.control<boolean>(false, Validators.required),
    fixPricePerMonth: this.fb.control<number>(null, Validators.min(0)),
    basePricePerDay: this.fb.control<number>(null, Validators.min(0)),
    sessionIsExtendable: this.fb.control<boolean>(true, Validators.required),
    maxParkingDurationInMinForm: this.fb.group<MaxParkingDurationInMinFormType>({
      days: this.fb.control<number>(null, Validators.min(0)),
      hours: this.fb.control<number>(null, Validators.min(0)),
      minutes: this.fb.control<number>(null, Validators.min(0))
    }),
    roundSessionFeeToXCents: this.fb.control<number>(1, [Validators.required, Validators.min(1)]),
    modifiedBy: this.fb.control<string>({value: null, disabled: true}),
    modifiedOn: this.fb.control<string>({value: null, disabled: true}),
    usage: this.fb.control<ParkingSubscriptionUsage>(ParkingSubscriptionUsage.ParkerOrForeignParker, Validators.required),
    paidBy: this.fb.control<ParkingSubscriptionPaidBy>(ParkingSubscriptionPaidBy.ParkingPerson, Validators.required),
    pcConfirmTime: this.fb.control<number>(null, Validators.min(0)),
    parkingQuotaHours: this.fb.control<number>(null, Validators.min(0)),
    parkingQuotaDuration: this.fb.control<ParkingQuotaDuration>(ParkingQuotaDuration.Week),
    orderWeight: this.fb.control<number>(0),
    validationFallbackSubscriptionId: this.fb.control<number>(null),
    id: this.fb.control<number | null>(null),
  });

  // eslint-disable-next-line @typescript-eslint/ban-types
  public animation: object = {
    previous: {effect: '', duration: 0, easing: ''},
    next: {effect: '', duration: 0, easing: ''},
  };

  public saveButtonDisabled = false;
  public isUsedInSessions: boolean = false;

  MessageVariant = MessageVariant;
  MessageSeverity = MessageSeverity;

  private formInitialValue;
  private subscriptions = new Subscription();
  private _detailInitInfo: DetailsInitInfo;
  private formChanged: boolean = false;

  constructor(private subscriptionService: SubscriptionDetailsService,
              private fb: FormBuilder,
              private toastService: ToastService,
              private modalService: ModalService,
              private clientService: ClientService) {
  }

  public get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public get cancelButtonText(): string {
    if (this.selectedSubscription) {
      return 'i18n.shared.cancel';
    }
    return 'i18n.shared.close';
  }

  public get isReadonly(): boolean {
    return false;
  }

  public ngOnInit(): void {
    this.setFieldDisabledStatus(this.subscriptionForm.controls.chargeCap, this.subscriptionForm.controls.chargeCapPerMonth.value);
    this.setFieldDisabledStatus(
      this.subscriptionForm.get('denyForeignParkingSubscriptionFallback'),
      this.subscriptionForm.get('type').value === ParkingSubscriptionType.StandardSubscription
    );

    this.formInitialValue = JSON.parse(JSON.stringify(this.subscriptionForm.value));
    this.subscriptions.add(this.subscriptionForm.statusChanges
      .pipe(
        startWith(this.subscriptionForm.status),
      )
      .subscribe(status => {
        const hasChanges = !(JSON.stringify(this.formInitialValue) === JSON.stringify(this.subscriptionForm.value));
        this.updateFormChangedState(hasChanges);
        this.saveButtonDisabled = (status !== 'VALID') || !hasChanges;
      }));

    //Listeners for disabled fields
    this.subscriptions.add(this.subscriptionForm.controls.chargeCapPerMonth.valueChanges.subscribe(value => {
      this.setFieldDisabledStatus(this.subscriptionForm.controls.chargeCap, value);
    }));
    this.subscriptions.add(this.subscriptionForm.controls.type.valueChanges.subscribe(value => {
      this.setFieldDisabledStatus(
        this.subscriptionForm.controls.denyForeignParkingSubscriptionFallback,
        this.subscriptionForm.controls.type.value === ParkingSubscriptionType.StandardSubscription
      );
      this.setFieldDisabledStatus(
        this.subscriptionForm.controls.usage,
        this.subscriptionForm.controls.type.value === ParkingSubscriptionType.ForeignParkerSubscription
      );
      this.setFieldDisabledStatus(
        this.subscriptionForm.controls.paidBy,
        this.subscriptionForm.controls.type.value === ParkingSubscriptionType.ForeignParkerSubscription
      );
      this.setFieldDisabledStatus(
        this.subscriptionForm.controls.pcConfirmTime,
        this.subscriptionForm.controls.type.value === ParkingSubscriptionType.ForeignParkerSubscription
      );
      if (this.subscriptionForm.controls.type.value !== ParkingSubscriptionType.StandardSubscription) {
        this.subscriptionForm.controls.denyForeignParkingSubscriptionFallback.setValue(false);
      } else {
        this.subscriptionForm.controls.usage.setValue(ParkingSubscriptionUsage.ParkerOrForeignParker);
        this.subscriptionForm.controls.paidBy.setValue(ParkingSubscriptionPaidBy.ParkingPerson);
        this.subscriptionForm.controls.pcConfirmTime.setValue(0);
      }
      if (value === ParkingSubscriptionType.ForeignParkerSubscription) {
        this.subscriptionForm.controls.multipleCarsPerUserTreatAs.setValue(MultipleCarsPerUserTreatAs.MultipleCars);
        this.setFieldDisabledStatus(this.subscriptionForm.controls.multipleCarsPerUserTreatAs, false);
      } else {
        this.setFieldDisabledStatus(this.subscriptionForm.controls.multipleCarsPerUserTreatAs, true);
      }
    }));
    FormHelper.updateTreeValidity(this.subscriptionForm, !!this.subscriptionForm.controls.name.value);
  }

  public ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  public onCancelClicked(): void {
    this.canDeactivate().subscribe(data => {
      if (data) {
        this.closeForm();
        this.cancel.emit();
      }
    });
  }

  public closeForm(): void {
    this.resetForm();
  }

  public submitForm(): void {
    if (this.selectedSubscription) {
      this.subscriptionService.updateParkingSubscription(this.getUpdateDTOFromFormData()).subscribe(data => {
        this.subscriptionUpdated.emit(data);
        this.selectedSubscription = data;
        this.updateForm();
      });
    }
    if (this.isAddSubscription) {
      this.subscriptionService.createParkingSubscription(this.getCreateDTOFromFormData()).subscribe(data => {
        this.closeForm();
        this.subscriptionCreated.emit(data);
      });
    }
  }

  public deleteSubscription(): void {
    const dialog = this.modalService.showOkCancelDialog('i18n.core.modal.delete.title', 'i18n.core.modal.delete.content');
    dialog.onOkSubject.subscribe(() => {
      this.subscriptionService.deleteSubscription(this.selectedSubscription.id).subscribe(data => {
        this.closeForm();
        this.subscriptionDeleted.emit(this.selectedSubscription.id);
      });
    });
  }

  public resetForm(): void {
    this.subscriptionForm.enable();
    this.subscriptionForm.reset({
      clientId: null,
      active: this._detailInitInfo.parentType === AdministrationItemType.Tariff ? true : false,
      assignable: this._detailInitInfo.parentType === AdministrationItemType.Tariff ? true : false,
      name: null,
      description: null,
      modifiedBy: null,
      modifiedOn: null,
      multipleCarsPerUserTreatAs: MultipleCarsPerUserTreatAs.Forbidden,
      currency: Currency.CHF,
      type: ParkingSubscriptionType.StandardSubscription,
      companyId: null,
      denyForeignParkingSubscriptionFallback: false,
      chargeCapPerMonth: false,
      chargeCap: null,
      chargeInRelationToEmploymentPercentage: false,
      fixPricePerMonth: null,
      basePricePerDay: null,
      sessionIsExtendable: true,
      maxParkingDurationInMinForm: ({
        days: null,
        hours: null,
        minutes: null
      }),
      roundSessionFeeToXCents: 1,
      usage: null,
      paidBy: null,
      pcConfirmTime: null,
      parkingQuotaHours: null,
      parkingQuotaDuration: ParkingQuotaDuration.Week,
      orderWeight: 0,
      validationFallbackSubscriptionId: null,
      id: null,
    });
    this.setFieldDisabledStatus(this.subscriptionForm.controls.active, this._detailInitInfo.parentType === AdministrationItemType.Tariff);
    this.setFieldDisabledStatus(this.subscriptionForm.controls.assignable, this._detailInitInfo.parentType === AdministrationItemType.Tariff);
    this.formInitialValue = JSON.parse(JSON.stringify(this.subscriptionForm.value));
    this.updateFormChangedState(false);
    this.subscriptionForm.markAsPristine();
    this.subscriptionForm.updateValueAndValidity();
  }

  public updateForm(): void {
    if (this.selectedSubscription) {
      this.subscriptionForm.enable();
      this.subscriptionForm.reset({
        clientId: this.selectedSubscription.clientId,
        active: this.selectedSubscription.active,
        assignable: this.selectedSubscription.assignable,
        name: this.selectedSubscription.name,
        description: this.selectedSubscription.description,
        modifiedBy: this.selectedSubscription.modifiedBy ?? null,
        modifiedOn: this.selectedSubscription.modifiedOn ? this.selectedSubscription.modifiedOn.toFormat('dd.MM.yyyy HH:mm:ss') : null,
        multipleCarsPerUserTreatAs: this.selectedSubscription.multipleCarsPerUserTreatAs,
        currency: this.selectedSubscription.currency,
        companyId: this.selectedSubscription.companyId,
        type: this.selectedSubscription.type,
        denyForeignParkingSubscriptionFallback: this.selectedSubscription.denyForeignParkingSubscriptionFallback,
        chargeCapPerMonth: this.selectedSubscription.chargeCapPerMonth,
        chargeCap: this.selectedSubscription.chargeCap,
        chargeInRelationToEmploymentPercentage: this.selectedSubscription?.chargeInRelationToEmploymentPercentage,
        fixPricePerMonth: this.selectedSubscription.fixPricePerMonth,
        basePricePerDay: this.selectedSubscription.basePricePerDay,
        sessionIsExtendable: this.selectedSubscription.sessionIsExtendable,
        maxParkingDurationInMinForm: MultiFieldDurationPickerHelper.createDurationMembers(this.selectedSubscription.maxParkingDurationInMin),
        roundSessionFeeToXCents: this.selectedSubscription.roundSessionFeeToXCents,
        usage: this.selectedSubscription.usage,
        paidBy: this.selectedSubscription.paidBy,
        pcConfirmTime: this.selectedSubscription.parkingCardConfirmationTimeInMinutes,
        parkingQuotaHours: this.selectedSubscription.parkingQuotaHours,
        parkingQuotaDuration: this.selectedSubscription.parkingQuotaDuration ?? ParkingQuotaDuration.Week,
        orderWeight: this.selectedSubscription.orderWeight ?? 0,
        validationFallbackSubscriptionId: this.selectedSubscription.validationFallbackSubscriptionId ?? null,
        id: this.selectedSubscription.id ?? null,
      });
      if (this.selectedSubscription.active || this.isUsedInSessions) {
        this.subscriptionForm.disable();
        this.setFieldDisabledStatus(this.subscriptionForm.controls.active, true);
        this.setFieldDisabledStatus(this.subscriptionForm.controls.assignable, this.selectedSubscription.tariffs?.length > 0);
      } else {
        this.setFieldDisabledStatus(this.subscriptionForm.controls.active, this.selectedSubscription.tariffs?.length > 0);
        this.setFieldDisabledStatus(this.subscriptionForm.controls.assignable, this.selectedSubscription.tariffs?.length > 0);
      }
      this.setFieldDisabledStatus(this.subscriptionForm.controls.name, true);
      this.setFieldDisabledStatus(this.subscriptionForm.controls.description, true);
      this.setFieldDisabledStatus(this.subscriptionForm.controls.pcConfirmTime, true);
      this.setFieldDisabledStatus(this.subscriptionForm.controls.companyId, true);
      this.setFieldDisabledStatus(this.subscriptionForm.controls.orderWeight, true);
      this.setFieldDisabledStatus(this.subscriptionForm.controls.validationFallbackSubscriptionId, true);

      this.formInitialValue = JSON.parse(JSON.stringify(this.subscriptionForm.value));
      this.updateFormChangedState(false);
      this.subscriptionForm.markAsPristine();
      this.subscriptionForm.updateValueAndValidity();
    } else {
      this.subscriptionForm.reset();
      this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message');
    }
  }

  private setFieldDisabledStatus(control: AbstractControl, isEnabled: boolean): void {
    if (isEnabled) {
      control.enable();
    } else {
      control.disable();
    }
  }

  private getUpdateDTOFromFormData(): UpdateParkingSubscriptionDTO {
    const rawValue = this.subscriptionForm.getRawValue();
    return new UpdateParkingSubscriptionDTO({
      id: this.selectedSubscription.id,
      active: rawValue.active,
      assignable: rawValue.assignable,
      name: rawValue.name,
      description: rawValue.description,
      companyId: rawValue.companyId,
      multipleCarsPerUserTreatAs: rawValue.multipleCarsPerUserTreatAs,
      currency: rawValue.currency,
      type: rawValue.type,
      denyForeignParkingSubscriptionFallback: rawValue.denyForeignParkingSubscriptionFallback,
      chargeCap: rawValue.chargeCap,
      chargeCapPerMonth: rawValue.chargeCapPerMonth,
      chargeInRelationToEmploymentPercentage: rawValue.chargeInRelationToEmploymentPercentage,
      fixPricePerMonth: rawValue.fixPricePerMonth,
      basePricePerDay: null,//rawValue.basePricePerDay, -> field is disabled
      sessionIsExtendable: rawValue.sessionIsExtendable,
      maxParkingDurationInMin: MultiFieldDurationPickerHelper.calculateTotalMinutes(
        rawValue.maxParkingDurationInMinForm.days,
        rawValue.maxParkingDurationInMinForm.hours,
        rawValue.maxParkingDurationInMinForm.minutes, true),
      roundSessionFeeToXCents: rawValue.roundSessionFeeToXCents,
      usage: rawValue.usage,
      paidBy: rawValue.paidBy,
      parkingCardConfirmationTimeInMinutes: rawValue.pcConfirmTime,
      parkingQuotaHours: rawValue.parkingQuotaHours,
      parkingQuotaDuration: rawValue.parkingQuotaDuration,
      orderWeight: rawValue.orderWeight,
      validationFallbackSubscriptionId: rawValue.validationFallbackSubscriptionId,
    });
  }

  private getCreateDTOFromFormData(): CreateParkingSubscriptionDTO {
    const rawValue = this.subscriptionForm.getRawValue();
    return new CreateParkingSubscriptionDTO({
      clientId: this.selectedClient.id,
      active: rawValue.active,
      assignable: rawValue.assignable,
      name: rawValue.name,
      description: rawValue.description,
      companyId: rawValue.companyId,
      multipleCarsPerUserTreatAs: rawValue.multipleCarsPerUserTreatAs,
      currency: rawValue.currency,
      type: rawValue.type,
      denyForeignParkingSubscriptionFallback: rawValue.denyForeignParkingSubscriptionFallback,
      chargeCap: rawValue.chargeCap,
      chargeCapPerMonth: rawValue.chargeCapPerMonth,
      chargeInRelationToEmploymentPercentage: rawValue.chargeInRelationToEmploymentPercentage,
      fixPricePerMonth: rawValue.fixPricePerMonth,
      basePricePerDay: null,//rawValue.basePricePerDay, -> field is disabled
      sessionIsExtendable: rawValue.sessionIsExtendable,
      maxParkingDurationInMin: MultiFieldDurationPickerHelper.calculateTotalMinutes(
        rawValue.maxParkingDurationInMinForm.days,
        rawValue.maxParkingDurationInMinForm.hours,
        rawValue.maxParkingDurationInMinForm.minutes, true),
      tariffIds: this._detailInitInfo.parentType === AdministrationItemType.Tariff ? [this._detailInitInfo.parentId] : null,
      structuralEntryId: this._detailInitInfo.parentType === AdministrationItemType.ConfigBasedStructuralElement ? this._detailInitInfo.parentId : null,
      roundSessionFeeToXCents: rawValue.roundSessionFeeToXCents,
      usage: rawValue.usage,
      paidBy: rawValue.paidBy,
      parkingCardConfirmationTimeInMinutes: rawValue.pcConfirmTime,
      parkingQuotaHours: rawValue.parkingQuotaHours,
      parkingQuotaDuration: rawValue.parkingQuotaDuration,
      orderWeight: rawValue.orderWeight,
      validationFallbackSubscriptionId: rawValue.validationFallbackSubscriptionId,
    });
  }

  private canDeactivate(): Observable<boolean> {
    if (this.formChanged) {
      const dialog = this.modalService.showOkCancelDialog('i18n.errors.discard-dialog.title', 'i18n.errors.discard-dialog.question');
      return merge(
        dialog.onOkSubject.pipe(map(() => true)),
        dialog.onCancelSubject.pipe(map(() => false)),
      );
    }
    return of(true);
  }

  private updateFormChangedState(hasChanges: boolean) {
    if (this.formChanged !== hasChanges) {
      this.formChanged = hasChanges;
      this.hasPendingDetailChangesChanged.emit(hasChanges);
    }
  }
}

